/**
 * User groups (Slack's "People → User groups", @team mentions): a named set of members with a handle (`@design`) that
 * mentions all of them at once, plus default channels new members of the group are added to. Core `logic/userGroups`
 * holds the rules (handle format and uniqueness, who may manage groups); the server parser resolves `@handle` (and
 * Slack's `<!subteam^ID>`) to a `mention_group` node, and a message notifies the group's members who are in the
 * conversation (guests outside it are never reached).
 *
 * Who can create, edit and disable groups is the workspace's `UserGroupPolicy` (Slack's "People who can manage user
 * groups"): `everyone` = every full member (not guests), `admins` = owners and admins only. Absent ⇒ `everyone`.
 * Disabled groups keep their members but can't be mentioned and are left out of autocomplete; they can be re-enabled.
 * Changes reach everyone as `user_group.updated` (the full group; replace by id) and `user_groups.policy_updated`.
 */
import { z } from "zod";
import { Id, Timestamp } from "./entities.ts";

/** Most members of one group, and most default channels (Slack's limits are larger; this keeps events small). */
export const MAX_GROUP_MEMBERS = 1000;
export const MAX_GROUP_CHANNELS = 20;

export const UserGroupPolicy = z.enum(["everyone", "admins"]);
export type UserGroupPolicy = z.infer<typeof UserGroupPolicy>;

export const UserGroup = z.object({
  id: Id,
  /** Without the `@`: lowercase letters, digits, `.`, `_` and `-` (core `normalizeGroupHandle`). Unique among groups and member handles. */
  handle: z.string().min(1).max(80),
  name: z.string().min(1).max(80),
  description: z.string().max(250).optional(),
  /** Members of the group (active people; agents can't be in groups). */
  memberIds: z.array(Id),
  /** Default channels: someone added to the group joins these (public channels, or private ones the editor is in). */
  channelIds: z.array(Id),
  createdBy: Id,
  createdAt: Timestamp,
  updatedAt: Timestamp,
  /** Set while disabled. */
  disabledAt: Timestamp.optional(),
});
export type UserGroup = z.infer<typeof UserGroup>;

/** GET /v1/w/:ws/usergroups — every group, disabled ones included (clients filter). */
export const ListUserGroupsResponse = z.object({ groups: z.array(UserGroup), policy: UserGroupPolicy });
export type ListUserGroupsResponse = z.infer<typeof ListUserGroupsResponse>;

/** POST /v1/w/:ws/usergroups */
export const CreateUserGroupRequest = z.object({
  name: z.string().min(1).max(80),
  handle: z.string().min(1).max(80),
  description: z.string().max(250).optional(),
  memberIds: z.array(Id).max(MAX_GROUP_MEMBERS).optional(),
  channelIds: z.array(Id).max(MAX_GROUP_CHANNELS).optional(),
});
export type CreateUserGroupRequest = z.infer<typeof CreateUserGroupRequest>;

/**
 * PATCH /v1/w/:ws/usergroups/:groupId — any subset. `description: ""` clears it; `disabled` disables or re-enables
 * the group; `memberIds` replaces the member list (Slack's usergroups.users.update): people added join its default
 * channels.
 */
export const UpdateUserGroupRequest = z.object({
  name: z.string().min(1).max(80).optional(),
  handle: z.string().min(1).max(80).optional(),
  description: z.string().max(250).optional(),
  memberIds: z.array(Id).max(MAX_GROUP_MEMBERS).optional(),
  channelIds: z.array(Id).max(MAX_GROUP_CHANNELS).optional(),
  disabled: z.boolean().optional(),
});
export type UpdateUserGroupRequest = z.infer<typeof UpdateUserGroupRequest>;

export const UserGroupResponse = z.object({ group: UserGroup });
export type UserGroupResponse = z.infer<typeof UserGroupResponse>;

/** PUT /v1/w/:ws/usergroups-policy — owners and admins. */
export const UserGroupPolicyRequest = z.object({ policy: UserGroupPolicy });
export type UserGroupPolicyRequest = z.infer<typeof UserGroupPolicyRequest>;

/** POST /v1/w/:ws/usergroups/:groupId/channels/join — I join the group's default channels I'm not in yet. */
export const JoinGroupChannelsResponse = z.object({ channelIds: z.array(Id) });
export type JoinGroupChannelsResponse = z.infer<typeof JoinGroupChannelsResponse>;
